import { useState, useEffect } from "react";
import { Loader2, X, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PARTICIPANT_TYPES, CLASS_TYPES, REG_MODES, GENDERS, REG_SOURCES, PROGRAMS, ctLabel, isMinor } from "@/lib/registrationMeta";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const labelCls = "text-xs font-semibold text-slate-500 mb-1 block";

function F({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

const empty = {
  full_name: "", nickname: "", participant_type: "anak", guardian_name: "", phone: "", guardian_phone: "",
  email: "", gender: "", birth_date: "", institution: "", class_or_job: "", program_interest: "",
  class_type: "", mode: "", desired_schedule: "", learning_goal: "", source: "", notes: "",
  consent: false, prospect_id: "", program_id: "",
};

export function RegistrationForm({ mode, initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...empty, ...(initial || {}), phone: initial?.phone_raw || initial?.phone || "", guardian_phone: initial?.guardian_phone_raw || "" });
  const [error, setError] = useState("");
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pq, setPq] = useState("");
  const [presults, setPresults] = useState([]);
  const [otherProgram, setOtherProgram] = useState(!!(initial?.program_interest) && !PROGRAMS.includes(initial.program_interest));
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    api.get("/programs", { params: { active: "true", page_size: 200 } })
      .then(({ data }) => {
        setPrograms(data.items);
        const names = data.items.map((p) => p.name);
        if (initial?.program_interest) setOtherProgram(!names.includes(initial.program_interest));
      })
      .catch(() => {});
  }, [initial]);
  const programOptions = programs.length ? programs.map((p) => p.name) : PROGRAMS;
  const selectedProgram = programs.find((p) => p.name === f.program_interest);
  const availCT = selectedProgram && selectedProgram.class_types?.length
    ? CLASS_TYPES.filter((c) => selectedProgram.class_types.includes(c.value)) : CLASS_TYPES;
  const availModes = selectedProgram && selectedProgram.modes?.length ? selectedProgram.modes : REG_MODES;
  const onProgramChange = (e) => {
    const v = e.target.value;
    if (v === "__lainnya__") { setOtherProgram(true); setF((p) => ({ ...p, program_interest: "", program_id: "" })); }
    else { setOtherProgram(false); const pr = programs.find((x) => x.name === v); setF((p) => ({ ...p, program_interest: v, program_id: pr ? pr.id : "" })); }
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const minor = isMinor(f.participant_type);

  const searchProspect = async () => {
    if (!pq.trim()) return;
    try { const { data } = await api.get("/prospects", { params: { q: pq, page: 1, page_size: 5 } }); setPresults(data.items); }
    catch { toast.error("Gagal mencari prospek."); }
  };
  const pickProspect = (p) => {
    setF((prev) => ({
      ...prev,
      full_name: p.full_name || p.institution_name || prev.full_name,
      participant_type: p.prospect_type === "lembaga" ? "perusahaan" : prev.participant_type,
      guardian_name: p.guardian_name || prev.guardian_name,
      phone: p.phone_raw || p.phone_normalized || prev.phone,
      email: p.email || prev.email,
      institution: p.school_or_company || prev.institution,
      class_or_job: p.class_or_job || prev.class_or_job,
      program_interest: p.program_interest || prev.program_interest,
      mode: p.mode || prev.mode,
      desired_schedule: p.desired_schedule || prev.desired_schedule,
      learning_goal: p.learning_goal || prev.learning_goal,
      source: prev.source || (p.source === "Chatbot Website" ? "Chatbot" : "Referensi teman/keluarga"),
      prospect_id: p.id,
    }));
    setPresults([]); setPq("");
    toast.success("Data prospek dimuat ke formulir.");
  };

  const save = async (force = false) => {
    setError(""); setLoading(true);
    try {
      const url = mode === "edit" ? `/registrations/${initial.id}?force=${force}` : `/registrations?force=${force}`;
      const method = mode === "edit" ? "put" : "post";
      const { data } = await api[method](url, f);
      toast.success(mode === "edit" ? "Pendaftaran diperbarui." : "Pendaftaran dibuat.");
      onSaved(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) {
        setDups(detail.duplicates); setError(detail.message || "Data duplikat terdeteksi.");
      } else { setError(formatApiErrorDetail(detail) || e.message); }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="registration-form">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-semibold text-slate-900">{mode === "edit" ? "Edit Pendaftaran" : "Pendaftaran Baru"}</h3>
          <button onClick={onClose} data-testid="registration-form-close" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="p-6 space-y-4">
          {mode !== "edit" && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <label className={labelCls}>Isi dari Prospek / Lead Chatbot (opsional)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input className={`${inputCls} pl-9`} placeholder="Cari nama / WA prospek…" value={pq} onChange={(e) => setPq(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchProspect(); } }} data-testid="reg-prospect-search" />
                </div>
                <button type="button" onClick={searchProspect} className="px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm font-medium">Cari</button>
              </div>
              {presults.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {presults.map((p) => (
                    <li key={p.id}><button type="button" onClick={() => pickProspect(p)} data-testid={`reg-prospect-pick-${p.id}`} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200">{p.full_name || p.institution_name} · {p.phone_normalized} · {p.program_interest || "-"}</button></li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Jenis Peserta">
              <select className={inputCls} value={f.participant_type} onChange={set("participant_type")} data-testid="reg-participant-type" required>
                {PARTICIPANT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </F>
            <F label="Nama Lengkap Calon Siswa"><input className={inputCls} value={f.full_name} onChange={set("full_name")} data-testid="reg-full-name" required /></F>
            <F label="Nama Panggilan"><input className={inputCls} value={f.nickname} onChange={set("nickname")} /></F>
            {minor && <F label="Nama Orang Tua / Wali"><input className={inputCls} value={f.guardian_name} onChange={set("guardian_name")} data-testid="reg-guardian-name" /></F>}
            <F label="Nomor WhatsApp Utama"><input className={inputCls} value={f.phone} onChange={set("phone")} placeholder="0812xxxx / 62812xxxx" data-testid="reg-phone" required /></F>
            {minor && <F label="WhatsApp Orang Tua/Wali (jika beda)"><input className={inputCls} value={f.guardian_phone} onChange={set("guardian_phone")} /></F>}
            <F label="Email"><input type="email" className={inputCls} value={f.email} onChange={set("email")} /></F>
            <F label="Jenis Kelamin"><select className={inputCls} value={f.gender} onChange={set("gender")}><option value="">—</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}</select></F>
            <F label="Tanggal Lahir"><input type="date" className={inputCls} value={f.birth_date} onChange={set("birth_date")} /></F>
            <F label="Sekolah / Kampus / Perusahaan / Instansi"><input className={inputCls} value={f.institution} onChange={set("institution")} /></F>
            <F label="Kelas / Pekerjaan"><input className={inputCls} value={f.class_or_job} onChange={set("class_or_job")} /></F>
            <F label="Program yang Diminati">
              <select className={inputCls} value={otherProgram ? "__lainnya__" : f.program_interest} data-testid="reg-program" required={!otherProgram} onChange={onProgramChange}>
                <option value="">— Pilih Program —</option>
                {programOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                <option value="__lainnya__">Lainnya…</option>
              </select>
              {otherProgram && <input className={`${inputCls} mt-2`} value={f.program_interest} onChange={set("program_interest")} placeholder="Tulis nama program" data-testid="reg-program-other" required />}
            </F>
            <F label="Jenis Kelas"><select className={inputCls} value={f.class_type} onChange={set("class_type")} data-testid="reg-class-type"><option value="">—</option>{availCT.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select></F>
            <F label="Metode Belajar"><select className={inputCls} value={f.mode} onChange={set("mode")}><option value="">—</option>{availModes.map((m) => <option key={m} value={m}>{m}</option>)}</select></F>
            <F label="Waktu Belajar yang Diinginkan"><input className={inputCls} value={f.desired_schedule} onChange={set("desired_schedule")} placeholder="Tulis hari & jam, cth: Senin & Rabu, 16.00–17.30" data-testid="reg-schedule" /></F>
            <F label="Sumber Informasi"><select className={inputCls} value={f.source} onChange={set("source")} data-testid="reg-source"><option value="">—</option>{REG_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></F>
            <F label="Tujuan Belajar"><input className={inputCls} value={f.learning_goal} onChange={set("learning_goal")} /></F>
          </div>

          <F label="Catatan Tambahan"><textarea className={inputCls} rows={2} value={f.notes} onChange={set("notes")} /></F>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.consent} onChange={(e) => setF((p) => ({ ...p, consent: e.target.checked }))} data-testid="reg-consent" className="mt-0.5" />
            <span>Calon siswa / wali menyetujui untuk dihubungi Admin IEC Mustikajaya.</span>
          </label>

          {error && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="registration-form-error">
              <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {error}</div>
              {dups.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {dups.map((d) => <li key={d.id}>{d.full_name} · {d.reg_no} · {d.phone_normalized}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg">Batal</button>
            {dups.length > 0 && (
              <button type="button" onClick={() => save(true)} disabled={loading} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="registration-save-force">Tetap Simpan Baru</button>
            )}
            <button type="submit" disabled={loading} className="flex-1 bg-[#0f2847] hover:bg-[#17416f] text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="registration-save-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
